import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Dna, MapPin, ShieldCheck, Sprout, LineChart, Handshake, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import SEO from "@/components/SEO";
import MarqueeStrip from "@/components/MarqueeStrip";
import EnquiryForm from "@/components/EnquiryForm";
import SocialModal from "@/components/SocialModal";
import Partners from "@/components/Partners";
import { IMAGES, JOURNEY, PIPELINE, WHY_PILLARS, STATS, TESTIMONIALS, HERO_VIDEOS } from "@/data/content";

const ICONS = { Dna, MapPin, ShieldCheck, Sprout, LineChart, Handshake };

const ROLL_WORDS = ["Advanced Genetics", "Higher Yields", "Hybrid Technology", "Farmer Prosperity", "Quality Seed"];

const HERO_TITLES = [
  ["Seed science that performs.", "Every season."],
  ["Advanced genetics that deliver.", "Higher yields."],
  ["Innovation that strengthens.", "Every harvest."],
  ["Better seeds that empower.", "Every farmer."],
  ["Stronger harvests that feed.", "Generations."],
];

const Hero = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const [titleIdx, setTitleIdx] = useState(0);
  const videoRefs = useRef([]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % HERO_VIDEOS.length), 7000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTitleIdx((i) => (i + 1) % HERO_TITLES.length), 4200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === active) v.play().catch(() => {});
      else v.pause();
    });
  }, [active]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      {HERO_VIDEOS.map((v, i) => (
        <video
          key={v.src}
          ref={(el) => (videoRefs.current[i] = el)}
          src={v.src}
          poster={v.poster}
          muted
          loop
          playsInline
          autoPlay={i === 0}
          preload={i === 0 ? "auto" : "metadata"}
          data-testid={`hero-video-${i}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ${i === active ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="hero-vignette absolute inset-0" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pt-40 pb-16 lg:px-10">
        <FadeUp>
          <p className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.35em] text-gold-300" data-testid="hero-eyebrow">
            <span className="inline-block h-px w-10 bg-gold-300" />
            Seed Park India — Hybrid Seed Science
          </p>
        </FadeUp>

        <div data-testid="hero-dialogue">
          <MaskedLines
            as="p"
            lines={["What grows a nation's prosperity?"]}
            delay={0.1}
            className="font-display text-2xl font-medium italic tracking-tight text-white/85 sm:text-3xl"
          />
          <div className="relative mt-3" data-testid="hero-title-slideshow">
            <AnimatePresence mode="wait">
              <motion.h1
                key={titleIdx}
                initial={{ y: 70, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -70, opacity: 0 }}
                transition={{ duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
                data-testid="hero-title-slide"
                className="font-display text-[11vw] font-black leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-8xl"
              >
                <span className="block">{HERO_TITLES[titleIdx][0]}</span>
                <span className="block text-gold-300">{HERO_TITLES[titleIdx][1]}</span>
              </motion.h1>
            </AnimatePresence>
          </div>
        </div>

        <FadeUp delay={0.65}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/80 md:text-lg">
            High-performance hybrids across maize, paddy, cotton, bajra, jowar, sunflower and wheat —
            engineered through intensive breeding and proven in farmers' fields across India.
          </p>
        </FadeUp>
        <FadeUp delay={0.8}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/products"
              data-testid="hero-explore-products-btn"
              className="group flex items-center gap-2 bg-brand-400 px-7 py-4 text-sm font-bold tracking-wide text-white transition-colors hover:bg-brand-500"
            >
              Explore Our Hybrids
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/research"
              data-testid="hero-research-btn"
              className="group flex items-center gap-2 border border-white/40 px-7 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:border-gold-300 hover:text-gold-300"
            >
              Inside Our R&D
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </FadeUp>
        <FadeUp delay={0.95}>
          <div className="mt-14 grid max-w-3xl grid-cols-2 gap-8 border-t border-white/15 pt-8 sm:grid-cols-4" data-testid="hero-stats">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-bold text-white">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">{s.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </motion.div>

      <div className="relative z-10 pb-8" data-testid="hero-rolling-text">
        <div className="w-[160%] overflow-visible whitespace-nowrap">
          <div className="animate-marquee-roll inline-flex items-center">
            {[0, 1].map((dup) => (
              <span key={dup} className="flex items-center" aria-hidden={dup === 1}>
                {ROLL_WORDS.map((w) => (
                  <span key={`${dup}-${w}`} className="flex items-center">
                    <span className="px-8 font-display text-4xl font-black tracking-tight text-outline-light sm:text-5xl">{w}</span>
                    <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" aria-hidden="true">
                      <path d="M6 0 L7.5 4.5 L12 6 L7.5 7.5 L6 12 L4.5 7.5 L0 6 L4.5 4.5 Z" fill="#F5B50A" />
                    </svg>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
        <div className="mx-auto mt-8 flex max-w-7xl items-center justify-end px-6 lg:px-10">
          <ChevronDown className="h-5 w-5 animate-bounce text-white/60" />
        </div>
      </div>
    </section>
  );
};

const FlagshipStrip = () => {
  const [packs, setPacks] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setPacks(r.data.filter((p) => p.packshot))).catch(() => {});
  }, []);
  if (!packs.length) return null;

  return (
    <section data-testid="flagship-strip" className="border-b border-[#123C6B]/10 bg-forest-900 py-16">
      <div className="mx-auto mb-10 flex max-w-7xl items-end justify-between px-6 lg:px-10">
        <div>
          <FadeUp>
            <p className="mb-3 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-corp-500">
              <span className="inline-block h-px w-8 bg-corp-500" /> Flagship Portfolio
            </p>
          </FadeUp>
          <MaskedLines as="h2" inView lines={["The packs farmers", "ask for by name."]} className="font-display text-3xl font-bold tracking-tight sm:text-4xl" />
        </div>
        <FadeUp delay={0.15}>
          <Link to="/products" data-testid="flagship-view-all" className="group hidden items-center gap-2 text-sm font-bold text-brand-400 sm:inline-flex">
            View all {packs.length} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
      <Marquee speed={35} gradient={false} pauseOnHover>
        {packs.map((p) => (
          <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`flagship-${p.slug}`} className="group mx-4 block">
            <div className="w-44 border border-[#123C6B]/10 bg-white p-4 shadow-[0_10px_30px_rgba(18,60,107,0.08)] transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-[0_20px_44px_rgba(18,60,107,0.16)]">
              <img src={p.image} alt={`${p.name} seed pack`} loading="lazy" className="h-44 w-full object-contain" />
              <p className="mt-3 font-display text-sm font-bold tracking-tight text-foreground">{p.name}</p>
            </div>
          </Link>
        ))}
      </Marquee>
    </section>
  );
};

const NewLaunches = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setItems(r.data.filter((p) => p.is_new))).catch(() => {});
  }, []);
  if (!items.length) return null;

  return (
    <section data-testid="new-launches-section" className="py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow="Just Launched"
            titleLines={["New hybrids,", "fresh from research."]}
            description="The latest releases from Seed Park breeding stations — now available through our dealer network."
          />
        </div>
        <div className="grid gap-6 md:grid-cols-3" data-testid="new-launches-grid">
          {items.map((p, i) => (
            <FadeUp key={p.slug} delay={i * 0.1} className="h-full">
              <Link
                to={`/products/${p.slug}`}
                data-testid={`new-launch-${p.slug}`}
                className="group relative flex h-full flex-col overflow-hidden border border-[#123C6B]/10 bg-forest-900 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(18,60,107,0.16)]"
              >
                <span className="absolute left-5 top-5 z-10 bg-gold-300 px-3 py-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-forest-950" data-testid={`new-launch-badge-${p.slug}`}>
                  New Launch
                </span>
                <div className="bg-white p-10">
                  <img src={p.image} alt={`${p.name} seed pack`} loading="lazy" className="h-64 w-full object-contain transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-3xl font-black tracking-tight group-hover:text-brand-400">{p.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-mist">{p.tagline}</p>
                  <div className="mt-6 flex items-center justify-end border-t border-[#123C6B]/10 pt-5">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-brand-400">
                      View Hybrid <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutTeaser = () => (
  <section data-testid="home-about-section" className="relative py-28 lg:py-36">
    <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-5">
        <FadeUp>
          <p className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-brand-400">
            <span className="inline-block h-px w-8 bg-brand-400" /> About Seed Park
          </p>
        </FadeUp>
        <MaskedLines
          as="h2"
          inView
          lines={["A research-driven", "seed company, built", "for Indian fields."]}
          className="font-display text-4xl font-bold tracking-tight sm:text-5xl"
        />
        <FadeUp delay={0.2}>
          <Link to="/about" data-testid="home-about-cta" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-brand-400">
            Our Story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
      <div className="lg:col-span-7">
        <FadeUp delay={0.15}>
          <p className="font-display text-2xl font-medium leading-snug text-foreground sm:text-3xl">
            We combine advanced genetics, intensive breeding and multi-location field testing to develop
            hybrids that deliver <span className="text-brand-400">higher yields</span>, greater adaptability
            and <span className="gold-underline">consistent field performance</span>.
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <div className="mt-8 flex max-w-2xl items-center gap-6 border border-[#123C6B]/10 bg-white p-6 shadow-[0_16px_44px_rgba(18,60,107,0.10)]">
            <img src="/assets/dsir-logo.png" alt="DSIR Recognized In-House R&D — Department of Scientific & Industrial Research, Govt. of India" className="h-28 w-28 shrink-0 object-contain" data-testid="home-dsir-badge" />
            <p className="text-base leading-relaxed text-mist">
              Backed by <span className="font-semibold text-foreground">DSIR-recognized R&amp;D labs</span> (Department of
              Scientific &amp; Industrial Research, Govt. of India), Seed Park unites scientific breeding expertise
              with extensive field-level evaluation — so every hybrid we release is proven across the diverse
              agro-climatic conditions Indian farmers actually face.
            </p>
          </div>
        </FadeUp>
        <FadeUp delay={0.3}>
          <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-4">
            {["Science", "Genetics", "Performance", "Trust"].map((w) => (
              <div key={w} className="bg-forest-900 px-4 py-6 text-center">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-400">{w}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const COUNT_WORDS = {
  "research-wheat": ["improved variety", "improved varieties"],
  "basmati-rice": ["variety", "varieties"],
  "improved-paddy": ["variety", "varieties"],
};

const CropsSection = () => {
  const [crops, setCrops] = useState([]);
  useEffect(() => {
    api.get("/crops").then((r) => setCrops(r.data)).catch(() => {});
  }, []);

  return (
    <section data-testid="home-crops-section" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Our Crops & Products"
          titleLines={["Nine crop families.", "One standard: performance."]}
          description="From cereals and fibre to millets and oilseeds — every Seed Park hybrid carries documented, multi-location performance data."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="crops-grid">
          {crops.map((crop, i) => (
            <FadeUp key={crop.slug} delay={(i % 4) * 0.08}>
              <Link
                to={`/products?crop=${crop.slug}`}
                data-testid={`crop-card-${crop.slug}`}
                className="group relative block aspect-[4/5] overflow-hidden border border-white/10"
              >
                <img src={crop.image} alt={crop.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-corp-700/95 via-corp-700/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-300">{crop.season}</p>
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-white">{crop.name}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/75 opacity-0 transition-opacity duration-300 group-hover:opacity-100">{crop.tagline}</p>
                  <p className="mt-3 flex items-center gap-1.5 text-xs font-bold text-gold-300">
                    {crop.product_count} {(COUNT_WORDS[crop.slug] || ["hybrid", "hybrids"])[crop.product_count === 1 ? 0 : 1]} <ArrowUpRight className="h-3.5 w-3.5" />
                  </p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div className="mt-12 text-center">
            <Link to="/products" data-testid="crops-view-all-btn" className="group inline-flex items-center gap-2 border border-brand-400 px-8 py-4 text-sm font-bold text-brand-400 transition-colors hover:bg-brand-400 hover:text-forest-950">
              View Full Portfolio <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="home-journey-section" className="py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading
        eyebrow="Research & Innovation"
        titleLines={["From genome to grain,", "in eight chapters."]}
        description="The Seed Park journey: a disciplined pipeline that turns advanced genetics into farmer prosperity."
      />
      <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {JOURNEY.map((ch, i) => (
          <FadeUp key={ch.num} delay={(i % 4) * 0.08} className="h-full">
            <div className="group flex h-full flex-col bg-forest-950 p-8 transition-colors hover:bg-forest-900" data-testid={`journey-chapter-${ch.num}`}>
              <p className="font-display text-5xl font-black text-outline transition-colors group-hover:text-brand-400 group-hover:[-webkit-text-stroke:0]">{ch.num}</p>
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{ch.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-sage">{ch.text}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Pipeline = () => (
  <section data-testid="home-pipeline-section" className="relative overflow-hidden bg-forest-900 py-28 lg:py-36">
    <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full border border-brand-400/10" />
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="Hybrid Development"
            titleLines={["Five seasons of proof", "before one release."]}
            description="A candidate hybrid travels a five-year pipeline before it reaches a farmer's field. Most never make it — that is the point."
          />
          <FadeUp>
            <div className="relative overflow-hidden border border-white/10">
              <img src={IMAGES.lab1} alt="Seed Park research scientists" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-950/70 to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-400">DSIR-Approved R&D Farm</p>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-7">
          <div className="flex flex-col">
            {PIPELINE.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.07}>
                <div className="group flex gap-6 border-b border-white/10 py-7 transition-colors hover:border-brand-400/40" data-testid={`pipeline-step-${i}`}>
                  <p className="w-20 shrink-0 font-mono text-xs uppercase tracking-[0.15em] text-gold-400">{p.step}</p>
                  <div>
                    <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-brand-400">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mist">{p.text}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <Link to="/research" data-testid="pipeline-research-cta" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-brand-400">
              Explore Research & Innovation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

const Trials = () => (
  <section data-testid="home-trials-section" className="relative overflow-hidden py-28 lg:py-36">
    <div className="absolute inset-0">
      <img src={IMAGES.aerial} alt="" loading="lazy" className="h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-forest-950 via-forest-950/80 to-forest-950" />
    </div>
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Multi-Location Trials"
            titleLines={["500+ trial sites.", "Every agro-climate", "that matters."]}
            description="A hybrid only earns the Seed Park name after proving itself across arid belts, irrigated deltas and rainfed uplands — season after season."
          />
        </div>
        <div className="lg:col-span-6">
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10">
            {[
              ["22+", "Agro-climatic zones covered"],
              ["5", "Zonal trial hubs"],
              ["3", "Seasons of evaluation minimum"],
              ["100%", "Data-backed release decisions"],
            ].map(([v, l], i) => (
              <FadeUp key={l} delay={i * 0.08}>
                <div className="bg-forest-950/90 p-8 backdrop-blur-sm">
                  <p className="font-display text-4xl font-black text-brand-400">{v}</p>
                  <p className="mt-2 text-sm text-mist">{l}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ProductionQuality = () => (
  <section data-testid="home-production-section" className="bg-forest-900 py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="Production & Quality"
            titleLines={["Every lot tested.", "Every seed accountable."]}
            description="From breeder seed multiplication to final packing, Seed Park lots pass physical purity, germination, vigour and genetic-purity testing in ISTA-aligned laboratories."
          />
          <div className="grid gap-5 sm:grid-cols-3">
            {[
              ["99%", "Physical purity benchmark"],
              ["95%+", "Germination standard"],
              ["2", "Seed processing plants"],
            ].map(([v, l], i) => (
              <FadeUp key={l} delay={i * 0.08}>
                <div className="border border-white/10 bg-forest-950 p-6">
                  <p className="font-display text-3xl font-black text-gold-400">{v}</p>
                  <p className="mt-2 text-xs leading-relaxed text-mist">{l}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <Link to="/production" data-testid="home-production-cta" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-brand-400">
              Inside Production & Quality <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <FadeUp delay={0.1}>
            <div className="relative overflow-hidden border border-white/10">
              <img src={IMAGES.lab2} alt="Seed quality laboratory" loading="lazy" className="aspect-[3/4] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-950/70 to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-400">Genetic Purity Lab</p>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

const WhySeedPark = () => (
  <section data-testid="home-why-section" className="py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading
        eyebrow="Why Seed Park"
        titleLines={["The MNC standard,", "the breeder's heart."]}
      />
      <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {WHY_PILLARS.map((p, i) => {
          const Icon = ICONS[p.icon];
          return (
            <FadeUp key={p.title} delay={(i % 3) * 0.08} className="h-full">
              <div className="group h-full bg-forest-950 p-8 transition-colors hover:bg-forest-900" data-testid={`why-pillar-${i}`}>
                <Icon className="h-7 w-7 text-brand-400 transition-transform duration-300 group-hover:scale-110" />
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-sage">{p.text}</p>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

const FarmersSection = ({ onOpenSocial }) => (
  <section data-testid="home-farmers-section" className="py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading
        eyebrow="Life With Farmers"
        titleLines={["The final laboratory", "is the farmer's field."]}
        description="Every Seed Park hybrid earns its place in the catalogue only after it has earned a farmer's trust in the field."
      />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {TESTIMONIALS.map((t, i) => (
          <FadeUp key={`t-${i}`} delay={i * 0.08} className="h-full">
            <figure className="group flex h-full flex-col overflow-hidden border border-white/10 bg-forest-900" data-testid={`testimonial-${i}`}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={t.image} alt={`${t.product} ${t.crop} with farmers`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-corp-700/90 via-corp-700/15 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-300">{t.crop}</p>
                  <p className="mt-1 font-display text-xl font-black tracking-tight text-white">{t.product}</p>
                </div>
              </div>
              <blockquote className="flex flex-1 flex-col p-5">
                <p className="flex-1 text-sm leading-relaxed text-mist">{t.caption}</p>
              </blockquote>
            </figure>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2}>
        <div className="mt-12 text-center">
          <button
            onClick={onOpenSocial}
            data-testid="farmers-social-btn"
            className="group inline-flex items-center gap-2 bg-brand-400 px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-500"
          >
            Click to know Seed Park's Life with Farmers <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </FadeUp>
    </div>
  </section>
);

const CareersContact = () => (
  <section data-testid="home-contact-section" className="relative overflow-hidden border-t border-white/10 bg-forest-900 py-28 lg:py-36">
    <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
      <div className="lg:col-span-6">
        <MaskedLines as="h2" inView lines={["Grow your career.", "Grow with farmers."]} className="font-display text-4xl font-bold tracking-tight sm:text-5xl" />
        <FadeUp delay={0.15}>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-mist">
            Join a team of breeders, scientists and field professionals building India's next generation
            of high-performance hybrids — or reach out to discuss seed partnerships and distribution.
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/careers" data-testid="home-careers-cta" className="group flex items-center gap-2 border border-brand-400 px-7 py-4 text-sm font-bold text-brand-400 transition-colors hover:bg-brand-400 hover:text-forest-950">
              View Open Roles <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="home-contact-cta" className="group flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold text-foreground transition-colors hover:border-brand-400 hover:text-brand-400">
              Contact Us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeUp>
      </div>
      <div className="lg:col-span-6">
        <FadeUp delay={0.1}>
          <EnquiryForm type="general" heading="Quick Enquiry" testId="home-enquiry" />
        </FadeUp>
      </div>
    </div>
  </section>
);

const Home = () => {
  const [socialOpen, setSocialOpen] = useState(false);
  return (
    <>
      <SEO
        description="High-performance hybrid seeds across maize, paddy, cotton, bajra, jowar, sunflower and wheat — bred through DSIR-recognised R&D and proven in farmers' fields across India. An MS Group company."
      />
      <Hero />
      <MarqueeStrip />
      <FlagshipStrip />
      <NewLaunches />
      <AboutTeaser />
      <CropsSection />
      <Manifesto />
      <Pipeline />
      <Trials />
      <ProductionQuality />
      <WhySeedPark />
      <MarqueeStrip dark />
      <Partners dark />
      <FarmersSection onOpenSocial={() => setSocialOpen(true)} />
      <CareersContact />
      <SocialModal open={socialOpen} onClose={() => setSocialOpen(false)} />
    </>
  );
};

export default Home;
